import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { useRouter } from "next/router"

import type { SQLNamespace } from "@codemirror/lang-sql"
import {
  FormField,
  GroupLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { openCoviWithPrompt } from "@/lib/coviEvents"
import { startSqlHandoff } from "@/lib/coviSqlHandoff"
import { AskSofiaButton } from "@/components/AskSofiaButton"
import { SqlEditor } from "@/components/SqlEditor"
import type {
  ColumnEnum,
  ColumnEnums,
  ColumnTypes,
} from "@/components/SqlSchemaPanel"
import { useSqlSchemaPanel } from "@/components/SqlSchemaPanelContext"
import type { MetricRecordData } from "@/types/metrics/contracts"

import { useSimulationDataFramesSchemaTransport } from "../routes/useSimulationDataFramesSchemaTransport"
import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import {
  isSqlAggregationMethod,
  SQL_AGGREGATION_METHOD_OPTIONS,
  SQL_UNIT_OPTIONS,
  type SqlAggregationMethod,
} from "../utils/sqlMetricOptions"
import {
  buildSqlMetricPrompt,
  parseCoviSqlReply,
} from "../utils/sqlMetricPrompt"
import { SqlTestArea } from "./SqlTestArea"

interface SqlConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

interface SqlTestRequest {
  routePath: string
  simulationOutputId?: string
  id: number
  sqlQuery: string
  aggregationMethod: SqlAggregationMethod
  unit?: string
}

/**
 * Config form for the SQL metric: a SQL query that runs against the simulation's
 * data frames, the aggregation method that reduces the query's per-timestamp
 * rows to a single value, and the unit that value is reported in.
 */
export const SqlConfig: React.FC<SqlConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const router = useRouter()
  const baseId = useId()
  const aggregationMethodId = `${baseId}-aggregation-method`
  const unitId = `${baseId}-unit`
  const routePath = router.asPath.split("?")[0]
  const { isOpen, setContent, toggle, close } = useSqlSchemaPanel()
  const {
    data: schemaResponse,
    isFetching: isSchemaFetching,
    refetch: refetchSchema,
  } = useSimulationDataFramesSchemaTransport()

  const [selection, setSelection] = useState<{
    routePath: string
    id: string | null
  }>()
  const selectedSimulationId =
    selection?.routePath === routePath ? selection.id : null
  const [requestedTest, setTestRequest] = useState<SqlTestRequest | null>(null)
  const handleSimulationChange = useCallback(
    (id: string | null) => {
      setSelection({ routePath, id })
      setTestRequest((request) =>
        request?.routePath === routePath && request.simulationOutputId === id
          ? request
          : null,
      )
    },
    [routePath],
  )
  const testRequest =
    requestedTest?.routePath === routePath ? requestedTest : null
  const nextTestRequestIdRef = useRef(0)

  const handleRetrySchema = useCallback(() => {
    void refetchSchema()
  }, [refetchSchema])

  const { schema, columnEnums, columnTypes } = useMemo(() => {
    const namespace: Record<string, string[]> = {}
    const enums: ColumnEnums = {}
    const types: ColumnTypes = {}
    for (const table of schemaResponse?.tables ?? []) {
      const columnNames = table.columns.map((column) => column.name)
      namespace[table.name] = columnNames
      const tableEnums: Record<string, ColumnEnum> = {}
      const tableTypes: Record<string, string> = {}
      for (const column of table.columns) {
        tableTypes[column.name] = column.sql_type
        if (column.enum) {
          tableEnums[column.name] = {
            name: column.enum.name,
            values: column.enum.values,
          }
        }
      }
      types[table.name] = tableTypes
      if (Object.keys(tableEnums).length > 0) {
        enums[table.name] = tableEnums
      }
    }
    return {
      schema: namespace as SQLNamespace,
      columnEnums: enums,
      columnTypes: types,
    }
  }, [schemaResponse])

  useEffect(() => {
    setContent({
      schema,
      columnEnums,
      columnTypes,
      isLoading: isSchemaFetching,
      onRetry: handleRetrySchema,
    })
  }, [
    setContent,
    schema,
    columnEnums,
    columnTypes,
    isSchemaFetching,
    handleRetrySchema,
  ])

  useEffect(() => {
    return () => {
      setContent(null)
      close()
    }
  }, [setContent, close])

  const metricMetadata = useMemo(
    () => metricRecordData.metric_metadata ?? {},
    [metricRecordData.metric_metadata],
  )
  const sqlQuery = metricMetadata.sql_query ?? ""
  const aggregationMethod: SqlAggregationMethod = isSqlAggregationMethod(
    metricMetadata.aggregation_method,
  )
    ? metricMetadata.aggregation_method
    : "average"
  const selectedUnitKey =
    SQL_UNIT_OPTIONS.find(
      (option) => option.metadataUnit === metricMetadata.unit,
    )?.key ?? "none"

  const updateMetadata = (metadata: MetricRecordData["metric_metadata"]) => {
    setTestRequest(null)
    onChange({ ...metricRecordData, metric_metadata: metadata })
  }

  const handleSqlQueryChange = (value: string) => {
    updateMetadata({ ...metricMetadata, sql_query: value })
  }

  const handleAggregationMethodChange = (value: string) => {
    if (isSqlAggregationMethod(value)) {
      updateMetadata({ ...metricMetadata, aggregation_method: value })
    }
  }

  const handleUnitChange = (key: string) => {
    const option = SQL_UNIT_OPTIONS.find((unitOption) => unitOption.key === key)
    if (option === undefined) {
      return
    }
    updateMetadata({ ...metricMetadata, unit: option.metadataUnit })
  }

  const handleTestMetric = () => {
    nextTestRequestIdRef.current += 1
    setTestRequest({
      id: nextTestRequestIdRef.current,
      routePath,
      simulationOutputId: selectedSimulationId ?? undefined,
      sqlQuery,
      aggregationMethod,
      unit: metricMetadata.unit,
    })
  }

  // Apply a Sofia reply: drop in the SQL and, when Sofia suggests them, the
  // metric name, description, aggregation method, and unit — merged into the
  // latest record (kept fresh via the ref so edits made after "Ask Sofia" aren't
  // clobbered).
  const applyReplyRef = useRef<(message: string) => void>(() => {})
  applyReplyRef.current = (message: string) => {
    const reply = parseCoviSqlReply(message)
    if (!reply.sql) {
      return
    }
    const nextMetadata = { ...metricMetadata, sql_query: reply.sql }
    if (reply.aggregationMethod) {
      nextMetadata.aggregation_method = reply.aggregationMethod
    }
    if (reply.unit) {
      nextMetadata.unit = reply.unit.metadataUnit
    }
    setTestRequest(null)
    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
      ...(reply.name ? { metric_display_name: reply.name } : {}),
      ...(reply.description ? { description: reply.description } : {}),
    })
  }

  // A registered handoff is what makes the Sofia panel show "Add to SQL"; tear it
  // down when the form unmounts so the control doesn't linger.
  const endHandoffRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    return () => {
      endHandoffRef.current?.()
    }
  }, [])

  // Open Sofia with the schema + query contract and let it ask the user what
  // they want to measure; the reply comes back through the handoff.
  const handleAskSofia = () => {
    endHandoffRef.current?.()
    endHandoffRef.current = startSqlHandoff({
      apply: (message) => applyReplyRef.current(message),
    })
    openCoviWithPrompt(
      buildSqlMetricPrompt({ tables: schemaResponse?.tables ?? [] }),
    )
  }

  return (
    <div className="space-y-4">
      <FormField>
        <div className={LABEL_ROW_STYLES}>
          <GroupLabel
            className={LABEL_STYLES}
            tooltip={
              <span>
                Query the simulation&apos;s data frames (open the schema panel
                for tables and columns). Return one row per data point with a
                numeric <code className="font-mono">value</code> column and a{" "}
                <code className="font-mono">start_offset_milliseconds</code>{" "}
                column (
                <code className="font-mono">end_offset_milliseconds</code> is
                optional) — no other columns are allowed, so alias your
                expression, e.g. <code className="font-mono">... AS value</code>
                . The aggregation method below reduces the{" "}
                <code className="font-mono">value</code> column to the
                metric&apos;s result.
              </span>
            }
          >
            SQL Query
          </GroupLabel>
          <div className="ml-auto flex items-center gap-2">
            <AskSofiaButton onClick={handleAskSofia} />
          </div>
        </div>
        <SqlEditor
          value={sqlQuery}
          onChange={handleSqlQueryChange}
          placeholder="SELECT start_offset_milliseconds, end_offset_milliseconds - start_offset_milliseconds AS value FROM transcript_messages"
          height="180px"
          ariaLabel="SQL query"
          schema={schema}
          onToggleSchemaPanel={toggle}
          isSchemaPanelOpen={isOpen}
        />
      </FormField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label
              htmlFor={aggregationMethodId}
              id={`${aggregationMethodId}-label`}
              className={LABEL_STYLES}
            >
              Aggregation Method
            </label>
          </div>
          <Select
            value={aggregationMethod}
            onValueChange={handleAggregationMethodChange}
          >
            <SelectTrigger
              id={aggregationMethodId}
              aria-labelledby={`${aggregationMethodId}-label`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SQL_AGGREGATION_METHOD_OPTIONS.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label
              htmlFor={unitId}
              id={`${unitId}-label`}
              className={LABEL_STYLES}
            >
              Unit
            </label>
          </div>
          <Select value={selectedUnitKey} onValueChange={handleUnitChange}>
            <SelectTrigger id={unitId} aria-labelledby={`${unitId}-label`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SQL_UNIT_OPTIONS.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <SqlTestArea
        onTest={handleTestMetric}
        canTest={sqlQuery.trim().length > 0}
        sqlQuery={testRequest?.sqlQuery ?? sqlQuery}
        testRequestId={testRequest?.id ?? null}
        testSimulationOutputId={testRequest?.simulationOutputId}
        selectedSimulationId={selectedSimulationId}
        onSimulationChange={handleSimulationChange}
        metricDisplayName={
          metricRecordData.metric_display_name || "Draft SQL metric"
        }
        aggregationMethod={testRequest?.aggregationMethod ?? aggregationMethod}
        unit={testRequest?.unit ?? metricMetadata.unit}
      />
    </div>
  )
}
